import { apiFetch, apiMutate } from '../csrf-fetch';

export interface ParcelaEntradaApi {
  readonly numero: string;
  readonly valor: string;
  readonly vencimento: string | null;
}

export interface PropostaFiscalEntradaApi {
  readonly id: string;
  readonly origem?: string;
  readonly chaveNfe: string;
  readonly estadoConferencia: string;
  readonly versao: number;
  readonly snapshotHash: string;
  readonly omieEstadoExterno: string | null;
  readonly aprovadaEm: string | null;
  /** Campos opcionais: a UI só anuncia vínculo quando o servidor os provar. */
  readonly omieNfeId?: string;
  readonly omieRecebimentoId?: string;
  readonly financeira?: PropostaFinanceiraEntradaApi;
  readonly parcelas?: readonly ParcelaEntradaApi[];
}

export interface PropostaFinanceiraEntradaApi extends PropostaFiscalEntradaApi {
  readonly parcelas: readonly ParcelaEntradaApi[];
}

export interface RotuloVinculoOmie {
  readonly rotulo: 'Já registrada na Omie';
  readonly identificador: string;
  readonly estado: string;
}

function texto(valor: unknown): string | null {
  return typeof valor === 'string' && valor.trim() ? valor.trim() : null;
}

/** Não deduz vínculo de estado parcial: ID externo e estado têm de chegar juntos da projeção do servidor. */
export function rotuloVinculoOmie(proposta: PropostaFiscalEntradaApi): RotuloVinculoOmie | null {
  const estado = texto(proposta.omieEstadoExterno);
  const identificador = texto(proposta.omieNfeId) ?? texto(proposta.omieRecebimentoId);
  return estado && identificador ? { rotulo: 'Já registrada na Omie', identificador, estado } : null;
}

export function podeAprovarFiscal(proposta: PropostaFiscalEntradaApi): boolean {
  return proposta.estadoConferencia !== 'DIVERGENTE' && proposta.estadoConferencia !== 'INCERTA' && proposta.versao > 0 && /^[a-f0-9]{64}$/.test(proposta.snapshotHash);
}

/** Parcela sem vencimento é ausência de dado, nunca uma data a ser calculada pelo navegador. */
export function podeAprovarParcelas(proposta: Pick<PropostaFinanceiraEntradaApi, 'parcelas' | 'estadoConferencia' | 'versao' | 'snapshotHash'>): boolean {
  return podeAprovarFiscal(proposta as PropostaFiscalEntradaApi) && proposta.parcelas.length > 0 && proposta.parcelas.every((parcela) => texto(parcela.valor) !== null && texto(parcela.vencimento) !== null);
}

export async function detalharPropostaFiscal(id: string): Promise<PropostaFiscalEntradaApi> {
  return apiFetch<PropostaFiscalEntradaApi>(`/api/v1/omie/entradas/fiscais/${encodeURIComponent(id)}`);
}

export async function detalharPropostaFinanceira(id: string): Promise<PropostaFinanceiraEntradaApi> {
  return apiFetch<PropostaFinanceiraEntradaApi>(`/api/v1/omie/entradas/financeiras/${encodeURIComponent(id)}`);
}

export function aprovarPropostaFiscal(proposta: PropostaFiscalEntradaApi): Promise<Response> {
  return apiMutate(`/api/v1/omie/entradas/fiscais/${encodeURIComponent(proposta.id)}/aprovacoes`, { method: 'POST', body: { versao: proposta.versao, snapshotHash: proposta.snapshotHash } });
}

export function aprovarPropostaFinanceira(proposta: PropostaFinanceiraEntradaApi): Promise<Response> {
  return apiMutate(`/api/v1/omie/entradas/financeiras/${encodeURIComponent(proposta.id)}/aprovacoes`, { method: 'POST', body: { versao: proposta.versao, snapshotHash: proposta.snapshotHash } });
}

/**
 * L-69 (17.1-27): quem aprova o fiscal mas não recebe nem gerencia Compras (o Faturamento) abre a conferência da NF de compra em modo
 * de revisão: o recebimento e o estorno ficam escondidos e só a revisão da proposta aparece. É ergonomia; o servidor é a barreira (403).
 */
export function emModoRevisaoFiscal(permissoes: readonly string[]): boolean {
  return permissoes.includes('financeiro:aprovar-fiscal') && !permissoes.includes('compras:receber') && !permissoes.includes('compras:gerenciar');
}
